import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BusinessHours, StoreProfile } from "@/types/store";
import { BusinessHoursEditor } from "./BusinessHoursEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { ReactNode } from "react";
import { Calendar } from "@/components/ui/calendar";
import { StoreImageUpload } from "./StoreImageUpload";

const defaultBusinessHours = {
  monday: { open: "09:00", close: "17:00", closed: false },
  tuesday: { open: "09:00", close: "17:00", closed: false },
  wednesday: { open: "09:00", close: "17:00", closed: false },
  thursday: { open: "09:00", close: "17:00", closed: false },
  friday: { open: "09:00", close: "17:00", closed: false },
  saturday: { open: "09:00", close: "17:00", closed: false },
  sunday: { open: "09:00", close: "17:00", closed: false },
};

interface DeliveryZone {
  radius: number;
  fee: number;
}

/**
 * Interface for defining delivery time slots
 * NOTE: The shape of this interface must match the database schema column 'non_sameday_time_slots'
 */
interface TimeSlot {
  /** Human-readable label for the time slot (e.g., "Morning", "Afternoon") */
  label: string;
  /** Start time in 24-hour format (HH:MM) */
  start_time: string;
  /** End time in 24-hour format (HH:MM) */
  end_time: string;
}

interface StoreSettingsFormData {
  store_name: string;
  description: string | null;
  phone: string | null;
  email: string | null;
  storefront_image_url: string | null;
  cover_image_url: string | null;
  use_storefront_as_cover: boolean;
  business_hours: BusinessHours;
  unit_number: string | null;
  street_number: string;
  street_name: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  delivery_radius: number;
  delivery_fee: number | null;
  minimum_order_amount: number | null;
  distance_type: "radius" | "driving" | "polygon" | "suburb";
  use_multiple_zones: boolean;
  delivery_zones: DeliveryZone[];
  same_day_enabled: boolean;
  delivery_days: string[];
  delivery_cutoff_times: Record<string, string>;
  non_sameday_enabled: boolean;
  non_sameday_days: string[];
  blackout_dates: string[];
}

interface StoreSettingsFormProps {
  initialData?: Partial<StoreProfile>;
  onSave: (data: StoreSettingsFormData) => Promise<void>;
  isLoading?: boolean;
  readOnly?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function CollapsibleSection({ 
  title, 
  description, 
  defaultOpen = false, 
  children 
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-md overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-gray-50 flex items-center justify-between text-left"
      >
        <div>
          <h2 className="text-sm font-medium text-gray-900">{title}</h2>
          {description && (
            <p className="text-xs text-gray-500">{description}</p>
          )}
        </div>
        <ChevronDown className={cn("h-4 w-4 text-gray-500 transition-transform", isOpen && "transform rotate-180")} />
      </button>
      {isOpen && <div className="p-3 border-t border-gray-200">{children}</div>}
    </div>
  );
}

function BlackoutDatePicker({ 
  value, 
  onChange, 
  disabled 
}: { 
  value?: Date; 
  onChange: (date: Date | undefined) => void; 
  disabled?: boolean;
}) {
  return (
    <Calendar
      mode="single"
      selected={value}
      onSelect={onChange}
      disabled={disabled}
      className="rounded-md border"
    />
  );
}

export function StoreSettingsForm({
  initialData,
  onSave,
  isLoading = false,
  readOnly = false,
}: StoreSettingsFormProps) {
  const { user } = useAuth();
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<StoreSettingsFormData>({
    defaultValues: {
      store_name: initialData?.store_name || "",
      description: initialData?.description || null,
      phone: initialData?.phone || null,
      email: initialData?.email || null,
      storefront_image_url: initialData?.storefront_image_url || null,
      cover_image_url: initialData?.cover_image_url || null,
      use_storefront_as_cover: initialData?.use_storefront_as_cover || false,
      business_hours: initialData?.business_hours || defaultBusinessHours,
      unit_number: initialData?.unit_number || null,
      street_number: initialData?.street_number || "",
      street_name: initialData?.street_name || "",
      suburb: initialData?.suburb || "",
      state: initialData?.state || "",
      postcode: initialData?.postcode || "",
      country: initialData?.country || "Australia",
      delivery_radius: initialData?.delivery_radius || 10,
      delivery_fee: initialData?.delivery_fee || null,
      minimum_order_amount: initialData?.minimum_order_amount || null,
      distance_type: initialData?.distance_type || "radius",
      use_multiple_zones: initialData?.use_multiple_zones || false,
      delivery_zones: initialData?.delivery_zones || [],
      same_day_enabled: initialData?.same_day_enabled || false,
      delivery_days: initialData?.delivery_days || [],
      delivery_cutoff_times: initialData?.delivery_cutoff_times || {},
      non_sameday_enabled: initialData?.non_sameday_enabled || true,
      non_sameday_days: initialData?.non_sameday_days || [],
      blackout_dates: initialData?.blackout_dates || []
    },
  });

  // Ensure all delivery days have cutoff times on initial load
  useEffect(() => {
    const deliveryDays = form.watch("delivery_days");
    const cutoffTimes = form.watch("delivery_cutoff_times");
    
    if (deliveryDays.length > 0) {
      const updatedCutoffTimes = { ...cutoffTimes };
      let needsUpdate = false;
      
      deliveryDays.forEach(day => {
        if (!cutoffTimes[day]) {
          updatedCutoffTimes[day] = "14:00"; // Default cutoff time
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        console.log("Setting default cutoff times for days:", deliveryDays);
        form.setValue("delivery_cutoff_times", updatedCutoffTimes, { shouldDirty: true });
      }
    }
  }, []);

  // Also ensure cutoff times are updated when delivery days change
  useEffect(() => {
    const deliveryDays = form.watch("delivery_days");
    const cutoffTimes = form.watch("delivery_cutoff_times");
    
    if (deliveryDays.length > 0) {
      const updatedCutoffTimes = { ...cutoffTimes };
      let needsUpdate = false;
      
      // Add cutoff times for new delivery days
      deliveryDays.forEach(day => {
        if (!updatedCutoffTimes[day]) {
          updatedCutoffTimes[day] = "14:00"; // Default cutoff time
          needsUpdate = true;
        }
      });
      
      // Remove cutoff times for days that are no longer delivery days
      Object.keys(updatedCutoffTimes).forEach(day => {
        if (!deliveryDays.includes(day)) {
          delete updatedCutoffTimes[day];
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        form.setValue("delivery_cutoff_times", updatedCutoffTimes, { shouldDirty: true });
      }
    }
  }, [form.watch("delivery_days")]);

  const businessHours = form.watch("business_hours") || defaultBusinessHours;
  /**
   * Watch the non_sameday_time_slots form field
   * IMPORTANT: This variable must use the same field name as the database column
   */
  const nonSamedaySlots = form.watch("non_sameday_time_slots");
  const sameDayWarning = form.watch("same_day_enabled") && form.watch("delivery_days").some(day => !form.watch("non_sameday_days").includes(day));

  /**
   * Add a new time slot to the non-same-day delivery options
   * Creates a new empty slot with default times and adds it to the form
   */
  const handleAddNonSamedaySlot = () => {
    const slots = [...nonSamedaySlots];
    slots.push({ label: "", start_time: "09:00", end_time: "17:00" });
    form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
  };

  const handleBusinessHoursChange = (
    day: string,
    hours: Partial<BusinessHours>
  ) => {
    console.log(`Updating business hours for ${day}:`, {
      current: businessHours[day],
      changes: hours
    });
    
    form.setValue(
      `business_hours.${day}`,
      { ...businessHours[day], ...hours },
      { shouldDirty: true, shouldTouch: true }
    );
    
    // Set local changes flag since form.formState.isDirty may not update immediately
    setHasLocalChanges(true);
  };

  const onSubmit = async (data: StoreSettingsFormData) => {
    try {
      setIsSaving(true);
      // Log form state
      console.log("Form is dirty:", form.formState.isDirty);
      console.log("Has local changes:", hasLocalChanges);
      console.log("Submitting form data:", data);
      
      // Get current form values
      const formValues = form.getValues();
      
      // Ensure all business hours data is properly formatted before saving
      const normalizedData = {
        ...formValues,
        // Normalize all day names to lowercase for consistency
        delivery_days: formValues.delivery_days.map(day => day.toLowerCase()),
        non_sameday_days: formValues.non_sameday_days.map(day => day.toLowerCase()),
        // Ensure all delivery days have cutoff times - normalize keys to lowercase
        delivery_cutoff_times: formValues.delivery_days.reduce((cutoffs, day) => {
          const normalizedDay = day.toLowerCase();
          cutoffs[normalizedDay] = formValues.delivery_cutoff_times?.[day] || formValues.delivery_cutoff_times?.[normalizedDay] || "14:00";
          return cutoffs;
        }, {} as Record<string, string>),
        business_hours: Object.keys(formValues.business_hours).reduce((acc, day) => {
          // Make sure we have the correct structure with all three properties
          acc[day] = {
            open: formValues.business_hours[day]?.open || "09:00",
            close: formValues.business_hours[day]?.close || "17:00",
            closed: formValues.business_hours[day]?.closed === true
          };
          console.log(`Normalized day ${day}:`, acc[day]);
          return acc;
        }, {} as Record<string, BusinessHours>)
      };
      
      // Log blackout dates specifically
      console.log("Blackout dates before saving:", formValues.blackout_dates);
      console.log("Blackout dates type:", typeof formValues.blackout_dates);
      console.log("Is blackout_dates an array?", Array.isArray(formValues.blackout_dates));
      
      // Ensure blackout_dates is always a proper array of strings with at least one element
      // This is to satisfy the database constraint: valid_blackout_dates CHECK (blackout_dates IS NULL OR blackout_dates != '{}')
      if (!Array.isArray(formValues.blackout_dates) || formValues.blackout_dates.length === 0) {
        console.warn("blackout_dates is empty or not an array, fixing...");
        // If empty, add a placeholder far-future date that won't affect business operations
        normalizedData.blackout_dates = ["2099-12-31"]; // Placeholder date far in the future
        console.log("Added placeholder date to satisfy database constraint");
      } else if (formValues.blackout_dates.some(date => typeof date !== 'string')) {
        console.warn("Some blackout dates are not strings, fixing...");
        // Ensure all elements are strings and there's at least one
        const validDates = formValues.blackout_dates
          .filter(date => date) // Filter out null/undefined
          .map(date => String(date)); // Convert to string
        
        normalizedData.blackout_dates = validDates.length > 0 ? validDates : ["2099-12-31"];
        console.log("Fixed blackout dates:", normalizedData.blackout_dates);
      }
      
      console.log("Saving business hours:", JSON.stringify(normalizedData.business_hours, null, 2));
      console.log("Saving blackout dates:", JSON.stringify(normalizedData.blackout_dates, null, 2));
      console.log("Saving delivery cutoff times:", JSON.stringify(normalizedData.delivery_cutoff_times, null, 2));
      console.log("Saving delivery_days (same-day):", JSON.stringify(normalizedData.delivery_days, null, 2));
      console.log("Saving non_sameday_days:", JSON.stringify(normalizedData.non_sameday_days, null, 2));
      console.log("Same day enabled:", normalizedData.same_day_enabled);
      console.log("Non-same day enabled:", normalizedData.non_sameday_enabled);
      
      await onSave(normalizedData);
      toast.success("Store settings saved successfully");
      setHasLocalChanges(false);
    } catch (error) {
      console.error("Error saving store settings:", error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : "Failed to save store settings. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, "0");
    return `${hour}:00`;
  });

  return (
    <form 
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form.getValues());
      }} 
      className="max-w-4xl mx-auto space-y-3"
    >
      <CollapsibleSection
        title="Basic Information"
        description="Your store's basic details"
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label htmlFor="store_name" className="text-xs font-medium text-gray-700">
              Store Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="store_name"
              {...form.register("store_name")}
              disabled={isLoading || readOnly}
              className={cn("mt-1 h-8 text-sm bg-white border-gray-200", {
                "border-red-300": form.formState.errors.store_name
              })}
            />
            {form.formState.errors.store_name?.message && (
              <p className="mt-1 text-xs text-red-500">
                {form.formState.errors.store_name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description" className="text-xs font-medium text-gray-700">Description</Label>
            <Textarea
              id="description"
              {...form.register("description")}
              disabled={isLoading || readOnly}
              className="mt-1 text-sm bg-white border-gray-200 h-16"
              placeholder="Tell customers about your store..."
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Store Images"
        description="Upload your profile picture and storefront image"
        defaultOpen={true}
      >
        <StoreImageUpload
          storefrontImageUrl={form.watch("storefront_image_url") || null}
          coverImageUrl={form.watch("cover_image_url") || null}
          useStorefrontAsCover={form.watch("use_storefront_as_cover")}
          onStorefrontImageChange={(url) => form.setValue("storefront_image_url", url, { shouldDirty: true })}
          onCoverImageChange={(url) => form.setValue("cover_image_url", url, { shouldDirty: true })}
          onUseStorefrontAsCoverChange={(value) => form.setValue("use_storefront_as_cover", value, { shouldDirty: true })}
          disabled={isLoading || readOnly}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Store Address"
        description="Your store's physical location"
      >
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-2">
            <Label htmlFor="unit_number" className="text-xs font-medium text-gray-700">Unit Number</Label>
            <Input
              id="unit_number"
              {...form.register("unit_number")}
              disabled={isLoading || readOnly}
              placeholder="Optional"
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>

          <div className="col-span-2">
            <Label htmlFor="street_number" className="text-xs font-medium text-gray-700">
              Street Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="street_number"
              {...form.register("street_number")}
              disabled={isLoading || readOnly}
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>

          <div className="col-span-2">
            <Label htmlFor="street_name" className="text-xs font-medium text-gray-700">
              Street Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="street_name"
              {...form.register("street_name")}
              disabled={isLoading || readOnly}
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>

          <div className="col-span-2">
            <Label htmlFor="suburb" className="text-xs font-medium text-gray-700">
              Suburb <span className="text-red-500">*</span>
            </Label>
            <Input
              id="suburb"
              {...form.register("suburb")}
              disabled={isLoading || readOnly}
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>

          <div className="col-span-2">
            <Label htmlFor="state" className="text-xs font-medium text-gray-700">
              State <span className="text-red-500">*</span>
            </Label>
            <Select
              value={form.watch("state")}
              onValueChange={(value) => form.setValue("state", value, { shouldDirty: true })}
              disabled={isLoading || readOnly}
            >
              <SelectTrigger className="mt-1 h-8 text-sm bg-white border-gray-200">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"].map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label htmlFor="postcode" className="text-xs font-medium text-gray-700">
              Postcode <span className="text-red-500">*</span>
            </Label>
            <Input
              id="postcode"
              {...form.register("postcode")}
              disabled={isLoading || readOnly}
              maxLength={4}
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Business Hours"
        description="Set your store's operating hours"
      >
        <div className="space-y-3">
          <BusinessHoursEditor
            businessHours={form.watch("business_hours")}
            onChange={handleBusinessHoursChange}
            disabled={isLoading || readOnly}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Contact Information"
        description="How customers can reach your store"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="phone" className="text-xs font-medium text-gray-700">Phone Number</Label>
            <Input
              id="phone"
              {...form.register("phone")}
              disabled={isLoading || readOnly}
              placeholder="+61 412345678"
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-xs font-medium text-gray-700">Email Address</Label>
            <Input
              id="email"
              type="email"
              {...form.register("email")}
              disabled={isLoading || readOnly}
              placeholder="store@example.com"
              className="mt-1 h-8 text-sm bg-white border-gray-200"
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Basic Delivery Settings"
        description="Set your delivery radius and fees"
      >
        <div className="grid grid-cols-3 gap-3">
          <div className="relative">
            <Label htmlFor="delivery_radius" className="text-xs font-medium text-gray-700">Delivery Radius</Label>
            <div className="mt-1 relative">
              <Input
                id="delivery_radius"
                type="number"
                {...form.register("delivery_radius", { valueAsNumber: true })}
                disabled={isLoading || readOnly}
                className="pr-8 bg-white border-gray-200"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">km</span>
            </div>
          </div>

          <div className="relative">
            <Label htmlFor="delivery_fee" className="text-xs font-medium text-gray-700">Delivery Fee</Label>
            <div className="mt-1 relative">
              <Input
                id="delivery_fee"
                type="number"
                step="0.01"
                {...form.register("delivery_fee", { valueAsNumber: true })}
                disabled={isLoading || readOnly}
                className="pl-6 bg-white border-gray-200"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
            </div>
          </div>

          <div className="relative">
            <Label htmlFor="minimum_order_amount" className="text-xs font-medium text-gray-700">Minimum Order</Label>
            <div className="mt-1 relative">
              <Input
                id="minimum_order_amount"
                type="number"
                step="0.01"
                {...form.register("minimum_order_amount", { valueAsNumber: true })}
                disabled={isLoading || readOnly}
                className="pl-6 bg-white border-gray-200"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Same Day Delivery"
        description="Configure same day delivery options"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={form.watch("same_day_enabled")}
              onCheckedChange={(checked) => {
                form.setValue("same_day_enabled", checked, { shouldDirty: true });
              }}
              disabled={isLoading || readOnly}
            />
            <Label className="text-xs font-medium text-gray-700">Enable same day delivery</Label>
          </div>

          {form.watch("same_day_enabled") && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Available Days</Label>
                <div className="grid grid-cols-4 gap-1">
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                    <label
                      key={day}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded transition-colors cursor-pointer",
                        form.watch("delivery_days").some(d => d.toLowerCase() === day.toLowerCase())
                          ? "bg-blue-50 text-blue-700 ring-1 ring-blue-700/10"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <Checkbox
                        id={`delivery_days_${day}`}
                        checked={form.watch("delivery_days").some(d => d.toLowerCase() === day.toLowerCase())}
                        onCheckedChange={(checked) => {
                          const currentDays = form.watch("delivery_days");
                          if (checked) {
                            // Normalize the day to add (first letter uppercase, rest lowercase)
                            const normalizedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
                            
                            // Check if day already exists (case-insensitive)
                            const alreadyExists = currentDays.some(
                              d => d.toLowerCase() === normalizedDay.toLowerCase()
                            );
                            
                            if (!alreadyExists) {
                              form.setValue("delivery_days", [...currentDays, normalizedDay], { shouldDirty: true });
                            }
                          } else {
                            // When removing, filter case-insensitively
                            form.setValue(
                              "delivery_days",
                              currentDays.filter((d) => d.toLowerCase() !== day.toLowerCase()),
                              { shouldDirty: true }
                            );
                          }
                        }}
                        disabled={isLoading || readOnly}
                      />
                      <span className="capitalize text-xs">{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Cutoff Times</Label>
                <div className="grid grid-cols-2 gap-1">
                  {form.watch("delivery_days").map((day) => (
                    <div key={day} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded">
                      <span className="capitalize text-xs min-w-[60px]">{day}</span>
                      <Select
                        value={form.watch(`delivery_cutoff_times.${day}`) || "14:00"}
                        onValueChange={(value) => {
                          const currentTimes = form.watch("delivery_cutoff_times");
                          form.setValue("delivery_cutoff_times", {
                            ...currentTimes,
                            [day]: value
                          }, { shouldDirty: true });
                        }}
                        disabled={isLoading || readOnly}
                      >
                        <SelectTrigger className="w-[80px] bg-white border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                            <SelectItem key={hour} value={`${hour.toString().padStart(2, "0")}:00`}>
                              {`${hour.toString().padStart(2, "0")}:00`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Non-Same Day Delivery"
        description="Configure advance delivery options"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={form.watch("non_sameday_enabled")}
              onCheckedChange={(checked) => {
                form.setValue("non_sameday_enabled", checked, { shouldDirty: true });
              }}
              disabled={isLoading || readOnly}
            />
            <Label className="text-xs font-medium text-gray-700">Enable non-same day delivery</Label>
          </div>

          {form.watch("non_sameday_enabled") && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Available Days</Label>
                <div className="grid grid-cols-4 gap-1">
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                    <label
                      key={day}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded transition-colors cursor-pointer",
                        form.watch("non_sameday_days").some(d => d.toLowerCase() === day.toLowerCase())
                          ? "bg-blue-50 text-blue-700 ring-1 ring-blue-700/10"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <Checkbox
                        id={`non_sameday_days_${day}`}
                        checked={form.watch("non_sameday_days").some(d => d.toLowerCase() === day.toLowerCase())}
                        onCheckedChange={(checked) => {
                          const currentDays = form.watch("non_sameday_days");
                          if (checked) {
                            // Normalize the day to add (first letter uppercase, rest lowercase)
                            const normalizedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
                            
                            // Check if day already exists (case-insensitive)
                            const alreadyExists = currentDays.some(
                              d => d.toLowerCase() === normalizedDay.toLowerCase()
                            );
                            
                            if (!alreadyExists) {
                              form.setValue("non_sameday_days", [...currentDays, normalizedDay], { shouldDirty: true });
                            }
                          } else {
                            // When removing, filter case-insensitively
                            form.setValue(
                              "non_sameday_days",
                              currentDays.filter((d) => d.toLowerCase() !== day.toLowerCase()),
                              { shouldDirty: true }
                            );
                          }
                        }}
                        disabled={isLoading || readOnly}
                      />
                      <span className="capitalize text-xs">{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Time Slots</Label>
                {/* 
                  Time Slots Component
                  IMPORTANT: non_sameday_time_slots must match the database column name
                  Do not rename without updating the database schema to match
                */}
                <div className="space-y-1">
                  {form.watch("non_sameday_time_slots")?.map((slot, index) => (
                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded group">
                      <Input
                        placeholder="Label (e.g. Morning)"
                        value={slot.label}
                        onChange={(e) => {
                          const slots = [...form.watch("non_sameday_time_slots")];
                          slots[index].label = e.target.value;
                          form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
                        }}
                        disabled={isLoading || readOnly}
                        className="w-[140px] bg-white border-gray-200"
                      />
                      <Select
                        value={slot.start_time}
                        onValueChange={(value) => {
                          const slots = [...form.watch("non_sameday_time_slots")];
                          slots[index].start_time = value;
                          form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
                        }}
                        disabled={isLoading || readOnly}
                      >
                        <SelectTrigger className="w-[80px] bg-white border-gray-200">
                          <SelectValue placeholder="Start" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-gray-500">to</span>
                      <Select
                        value={slot.end_time}
                        onValueChange={(value) => {
                          const slots = [...form.watch("non_sameday_time_slots")];
                          slots[index].end_time = value;
                          form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
                        }}
                        disabled={isLoading || readOnly}
                      >
                        <SelectTrigger className="w-[80px] bg-white border-gray-200">
                          <SelectValue placeholder="End" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const slots = form.watch("non_sameday_time_slots").filter((_, i) => i !== index);
                          form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
                        }}
                        disabled={isLoading || readOnly}
                        className="opacity-0 group-hover:opacity-100 h-6 w-6 hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const slots = [...form.watch("non_sameday_time_slots") || []];
                      slots.push({ label: "", start_time: "09:00", end_time: "17:00" });
                      form.setValue("non_sameday_time_slots", slots, { shouldDirty: true });
                    }}
                    disabled={isLoading || readOnly}
                    className="w-full border-dashed"
                  >
                    Add Time Slot
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Blackout Dates"
        description="Set dates when delivery is not available"
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {form.watch("blackout_dates").length > 0 && (
              <div className="w-full bg-yellow-50 border border-yellow-200 p-2 mb-2 rounded text-xs text-yellow-700">
                <strong>Note:</strong> Due to a database constraint, at least one blackout date must always be present. 
                If you want to remove all blackout dates, please contact support.
              </div>
            )}
            
            {form.watch("blackout_dates").map((date, index) => (
              <div key={index} className="flex items-center gap-1 bg-gray-50 rounded px-2 py-0.5">
                <span className="text-xs text-gray-700">{date}</span>
                {form.watch("blackout_dates").length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const dates = form.watch("blackout_dates").filter((_, i) => i !== index);
                      form.setValue("blackout_dates", dates, { shouldDirty: true });
                    }}
                    className="h-4 w-4 hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-2 w-2" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <BlackoutDatePicker
            value={undefined}
            onChange={(date) => {
              if (date) {
                const formattedDate = format(date, "yyyy-MM-dd");
                const dates = [...form.watch("blackout_dates") || []];
                if (!dates.includes(formattedDate)) {
                  dates.push(formattedDate);
                  form.setValue("blackout_dates", dates, { shouldDirty: true });
                }
              }
            }}
            disabled={isLoading || readOnly}
          />
        </div>
      </CollapsibleSection>

      {/* Warning Messages */}
      {(form.watch("same_day_enabled") && !form.watch("non_sameday_enabled")) || 
       (form.watch("same_day_enabled") && form.watch("delivery_days").some(day => !form.watch("non_sameday_days").includes(day))) && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5">
          <h3 className="text-xs font-medium text-amber-800 mb-1.5">Attention Required</h3>
          <ul className="list-disc list-inside space-y-1 text-xs text-amber-700">
            {form.watch("same_day_enabled") && !form.watch("non_sameday_enabled") && (
              <li>
                You have same day delivery enabled but non-same day delivery is disabled. This might be a mistake.
              </li>
            )}
            {form.watch("same_day_enabled") && form.watch("delivery_days").some(day => !form.watch("non_sameday_days").includes(day)) && (
              <li>
                Some same day delivery days are not enabled for non-same day delivery.
              </li>
            )}
          </ul>
        </div>
      )}

      {!readOnly && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 mt-6 -mx-3">
          <div className="max-w-4xl mx-auto flex justify-end">
            <Button
              type="button"
              disabled={isLoading || isSaving}
              data-action="save-store-settings"
              className="px-4 py-1.5 h-8 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              onClick={() => {
                console.log("Save button clicked directly");
                console.log("Form state:", {
                  isDirty: form.formState.isDirty,
                  hasLocalChanges,
                  businessHours: form.getValues().business_hours
                });
                onSubmit(form.getValues());
              }}
            >
              {(isLoading || isSaving) ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  <span className="text-sm">Saving (this may take a few seconds)...</span>
                </>
              ) : (
                <span className="text-sm">Save Changes</span>
              )}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}